import React, { useMemo } from "react";

function SectionCard({ title, iconClass, children, highlight }) {
  return (
    <div
      className={`card shadow-sm border-0 rounded-3 mb-3 sku-form-section ${highlight ? "border border-warning border-2" : ""}`}
    >
      <div className="card-header bg-white border-0 border-bottom py-2 px-3">
        <h6 className="mb-0 fw-semibold text-body-secondary d-flex align-items-center gap-2 text-uppercase small letter-spacing-wide">
          {iconClass ? <i className={`bi ${iconClass} text-warning fs-6`} aria-hidden /> : null}
          <span>{title}</span>
        </h6>
      </div>
      <div className="card-body px-3 py-2">{children}</div>
    </div>
  );
}

function FormRow({ children, className = "" }) {
  return <div className={`row g-3 ${className}`}>{children}</div>;
}

function FieldLabel({ htmlFor, label, required, tooltip }) {
  return (
    <label htmlFor={htmlFor} className="form-label fw-semibold small mb-1 text-body-secondary">
      {label}
      {required ? <span className="text-danger ms-1">*</span> : null}
      {tooltip ? (
        <span className="ms-1 text-muted" title={tooltip} role="note">
          <i className="bi bi-info-circle small" aria-hidden />
        </span>
      ) : null}
    </label>
  );
}

const UOM_LIST = ["EA", "PCS", "KG", "LB", "G", "L", "ML", "CASE", "CARTON", "PALLET"];

const CATCH_DATA_FIELDS = [
  { suffix: "SerialNo", label: "Serial No (SR)", required: true },
  { suffix: "Imei", label: "IMEI", required: true },
  { suffix: "Other3", label: "Other 3", required: false },
  { suffix: "Other4", label: "Other 4", required: false },
  { suffix: "Other5", label: "Other 5", required: false },
];

function CatchDataFields({ prefix, title, formData, handleChange, errors, invalid }) {
  return (
    <div className="mt-3">
      <div className="small fw-semibold text-body-secondary mb-2">{title}</div>
      <FormRow>
        {CATCH_DATA_FIELDS.map((field) => {
          const name = `${prefix}${field.suffix}`;
          return (
            <div className="col-md-4 col-lg" key={name}>
              <FieldLabel htmlFor={name} label={field.label} required={field.required} />
              <input
                id={name}
                name={name}
                type="text"
                autoComplete="off"
                className={`form-control form-control-sm ${invalid(name)}`}
                value={formData[name] ?? ""}
                onChange={handleChange}
              />
              {errors[name] ? <div className="invalid-feedback d-block">{errors[name]}</div> : null}
            </div>
          );
        })}
      </FormRow>
    </div>
  );
}

export default function TabPackagingUom({ formData, handleChange, errors, invalid }) {
  const inner = !!(formData.innerPackUom && String(formData.innerPackUom).trim());
  const outer = !!(formData.outerPackUom && String(formData.outerPackUom).trim());

  const autoPiecesOuter = useMemo(() => {
    const a = parseFloat(String(formData.piecesPerInnerPack || ""));
    const b = parseFloat(String(formData.innerPacksPerOuterPack || ""));
    if (!Number.isFinite(a) || !Number.isFinite(b) || a <= 0 || b <= 0) return "";
    return String(a * b);
  }, [formData.piecesPerInnerPack, formData.innerPacksPerOuterPack]);

  const piecesOuterDisplay =
    String(formData.piecesPerOuterPack || "").trim() || (outer && inner ? autoPiecesOuter : "");

  return (
    <>
      <SectionCard title="Weight / Data" iconClass="bi-speedometer2">
        <FormRow>
          <div className="col-md-6">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="inboundCatchData"
                name="inboundCatchData"
                checked={!!formData.inboundCatchData}
                onChange={handleChange}
              />
              <label className="form-check-label small" htmlFor="inboundCatchData">
                Inbound Catch Data
              </label>
            </div>
          </div>
          <div className="col-md-6">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="outboundCatchData"
                name="outboundCatchData"
                checked={!!formData.outboundCatchData}
                onChange={handleChange}
              />
              <label className="form-check-label small" htmlFor="outboundCatchData">
                Outbound Catch Data
              </label>
            </div>
          </div>
        </FormRow>
        {formData.inboundCatchData ? (
          <CatchDataFields
            prefix="inbound"
            title="Inbound"
            formData={formData}
            handleChange={handleChange}
            errors={errors}
            invalid={invalid}
          />
        ) : null}
        {formData.outboundCatchData ? (
          <CatchDataFields
            prefix="outbound"
            title="Outbound"
            formData={formData}
            handleChange={handleChange}
            errors={errors}
            invalid={invalid}
          />
        ) : null}
        <FormRow className="mt-3">
          <div className="col-md-4">
            <FieldLabel htmlFor="voiceGroupingId" label="Voice grouping ID" />
            <input
              id="voiceGroupingId"
              name="voiceGroupingId"
              type="text"
              className="form-control form-control-sm"
              value={formData.voiceGroupingId ?? ""}
              onChange={handleChange}
            />
          </div>
        </FormRow>
      </SectionCard>

      <SectionCard title="UOM structure" iconClass="bi-boxes" highlight>
        <FormRow>
          <div className="col-md-4">
            <FieldLabel htmlFor="baseUom" label="Base UOM" required tooltip="Smallest sellable / stock unit." />
            <select
              id="baseUom"
              name="baseUom"
              className={`form-select form-select-sm ${invalid("baseUom")}`}
              value={formData.baseUom || ""}
              onChange={handleChange}
            >
              <option value="">Select…</option>
              {UOM_LIST.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
            {errors.baseUom ? <div className="invalid-feedback d-block">{errors.baseUom}</div> : null}
          </div>
          <div className="col-md-4">
            <FieldLabel htmlFor="innerPackUom" label="Inner Pack UOM" />
            <select id="innerPackUom" name="innerPackUom" className="form-select form-select-sm" value={formData.innerPackUom} onChange={handleChange}>
              <option value="">None</option>
              {UOM_LIST.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-4">
            <FieldLabel htmlFor="outerPackUom" label="Outer Pack UOM" />
            <select id="outerPackUom" name="outerPackUom" className="form-select form-select-sm" value={formData.outerPackUom} onChange={handleChange}>
              <option value="">None</option>
              {UOM_LIST.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </div>
          <div className="col-md-4">
            <FieldLabel htmlFor="palletUom" label="Pallet UOM" />
            <select id="palletUom" name="palletUom" className="form-select form-select-sm" value={formData.palletUom} onChange={handleChange}>
              <option value="">None</option>
              {UOM_LIST.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>
          </div>
        </FormRow>
      </SectionCard>

      <SectionCard title="Conversions" iconClass="bi-arrow-left-right" highlight>
        <FormRow>
          <div className="col-md-3">
            <FieldLabel htmlFor="piecesPerInnerPack" label="Pieces per Inner Pack" required={inner} />
            <input
              id="piecesPerInnerPack"
              name="piecesPerInnerPack"
              type="number"
              min="0"
              step="any"
              disabled={!inner}
              className={`form-control form-control-sm ${invalid("piecesPerInnerPack")}`}
              value={formData.piecesPerInnerPack}
              onChange={handleChange}
            />
            {errors.piecesPerInnerPack ? <div className="invalid-feedback d-block">{errors.piecesPerInnerPack}</div> : null}
          </div>
          <div className="col-md-3">
            <FieldLabel htmlFor="innerPacksPerOuterPack" label="Inner Packs per Outer" required={outer} />
            <input
              id="innerPacksPerOuterPack"
              name="innerPacksPerOuterPack"
              type="number"
              min="0"
              step="any"
              disabled={!outer}
              className={`form-control form-control-sm ${invalid("innerPacksPerOuterPack")}`}
              value={formData.innerPacksPerOuterPack}
              onChange={handleChange}
            />
            {errors.innerPacksPerOuterPack ? <div className="invalid-feedback d-block">{errors.innerPacksPerOuterPack}</div> : null}
          </div>
          <div className="col-md-3">
            <FieldLabel htmlFor="piecesPerOuterPack" label="Pieces per Outer Pack" tooltip="Auto-calculated when possible." />
            <input
              id="piecesPerOuterPack"
              name="piecesPerOuterPack"
              type="number"
              min="0"
              step="any"
              disabled={!outer}
              className={`form-control form-control-sm ${invalid("piecesPerOuterPack")}`}
              value={piecesOuterDisplay}
              onChange={handleChange}
            />
            {errors.piecesPerOuterPack ? <div className="invalid-feedback d-block">{errors.piecesPerOuterPack}</div> : null}
          </div>
          <div className="col-md-3">
            <FieldLabel htmlFor="outerPacksPerPallet" label="Outer Packs per Pallet" />
            <input
              id="outerPacksPerPallet"
              name="outerPacksPerPallet"
              type="number"
              min="0"
              step="any"
              className="form-control form-control-sm"
              value={formData.outerPacksPerPallet}
              onChange={handleChange}
            />
          </div>
        </FormRow>
      </SectionCard>

      <SectionCard title="Toggles & quantity controls" iconClass="bi-sliders">
        <FormRow className="align-items-center">
          <div className="col-md-4">
            <div className="form-check form-switch">
              <input className="form-check-input" type="checkbox" role="switch" id="catchWeightFlag" name="catchWeightFlag" checked={formData.catchWeightFlag} onChange={handleChange} />
              <label className="form-check-label small" htmlFor="catchWeightFlag">
                Catch weight flag
              </label>
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-check form-switch">
              <input className="form-check-input" type="checkbox" role="switch" id="uomConversionAllowed" name="uomConversionAllowed" checked={formData.uomConversionAllowed} onChange={handleChange} />
              <label className="form-check-label small" htmlFor="uomConversionAllowed">
                UOM conversion allowed
              </label>
            </div>
          </div>
          <div className="col-md-4">
            <div className="form-check form-switch">
              <input className="form-check-input" type="checkbox" role="switch" id="decimalAllowed" name="decimalAllowed" checked={formData.decimalAllowed} onChange={handleChange} />
              <label className="form-check-label small" htmlFor="decimalAllowed">
                Decimal allowed
              </label>
            </div>
          </div>
          <div className="col-md-3">
            <FieldLabel htmlFor="minOrderQty" label="Min order qty" />
            <input id="minOrderQty" name="minOrderQty" type="number" min="0" step="any" className="form-control form-control-sm" value={formData.minOrderQty} onChange={handleChange} />
          </div>
          <div className="col-md-3">
            <FieldLabel htmlFor="maxOrderQty" label="Max order qty" />
            <input id="maxOrderQty" name="maxOrderQty" type="number" min="0" step="any" className="form-control form-control-sm" value={formData.maxOrderQty} onChange={handleChange} />
          </div>
          <div className="col-md-3">
            <FieldLabel htmlFor="minReceiptQty" label="Min receipt qty" />
            <input id="minReceiptQty" name="minReceiptQty" type="number" min="0" step="any" className="form-control form-control-sm" value={formData.minReceiptQty} onChange={handleChange} />
          </div>
          <div className="col-md-3">
            <FieldLabel htmlFor="packConfigVersion" label="Pack configuration version" />
            <input id="packConfigVersion" name="packConfigVersion" type="text" className="form-control form-control-sm" value={formData.packConfigVersion} onChange={handleChange} />
          </div>
        </FormRow>
      </SectionCard>
    </>
  );
}
